/**
 * #40 · Rollos: el inventario físico de etiquetas.
 *
 * La columna que pide el criterio de aceptación es el **rango de series** de
 * cada rollo. Es la identidad operativa del rollo frente al impresor y frente
 * al depósito: §5 de la especificación al proveedor exige que cada rollo lleve
 * un rango contiguo y que los rangos nunca se solapen entre rollos, órdenes ni
 * clientes. Sin esa columna, un rollo no se puede conciliar contra un remito.
 *
 * Sobre la regla de "nunca renderizar el `unit_id`": lo que se muestra son los
 * dos **bordes** del rango, no la identidad de ninguna unidad. En ningún lugar
 * de estos módulos hay una unidad individual identificada por su id interno;
 * para eso está el código público. Ver `docs/adr-inbox/b6-catalogo.md`.
 */
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useKeysetQuery } from '@/api/hooks';
import { rollKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { SelectField } from '../products/ui/fields';
import {
  ROLL_STATES,
  ROLL_STATE_LABEL_KEYS,
  ROLL_STATE_TONES,
  isRollState,
  serialRange,
} from './custody';
import { LabelsTabs } from './LabelsTabs';

type Roll = Schemas['RollOut'];

const PAGE_SIZE = 50;

export function RollsPage(): ReactNode {
  const { t, fmt } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawState = searchParams.get('state');
  const state = rawState !== null && isRollState(rawState) ? rawState : null;
  const rawOrder = searchParams.get('order');
  const orderId = rawOrder !== null && /^\d+$/.test(rawOrder) ? Number(rawOrder) : null;

  const rolls = useKeysetQuery<Roll>(
    rollKeys.list({ limit: PAGE_SIZE, state, orderId }),
    '/api/v1/rolls',
    { state, order_id: orderId },
    { limit: PAGE_SIZE },
  );
  const { query } = rolls;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <LabelsTabs active="rolls" />

      <Card>
        <CardHeader
          title={t('labels.rolls.title')}
          description={t('labels.rolls.description')}
          actions={
            rolls.items.length > 0 ? (
              <Badge tone="neutral">{t('labels.rolls.count', { count: rolls.items.length })}</Badge>
            ) : undefined
          }
        />

        <CardBody className="border-b border-border">
          <SelectField
            label={t('labels.rolls.filter.state')}
            hint={t('labels.rolls.filter.state.hint')}
            value={state ?? ''}
            onChange={(value) => {
              const next = new URLSearchParams(searchParams);
              if (value === '') next.delete('state');
              else next.set('state', value);
              setSearchParams(next, { replace: true });
            }}
            options={[
              { value: '', label: t('labels.rolls.filter.all') },
              ...ROLL_STATES.map((value) => ({ value, label: t(ROLL_STATE_LABEL_KEYS[value]) })),
            ]}
          />
        </CardBody>

        {query.isPending && <LoadingState rows={6} />}

        {query.isError && (
          <CardBody>
            <ErrorState
              error={query.error}
              onRetry={() => {
                void query.refetch();
              }}
            />
          </CardBody>
        )}

        {!query.isPending && !query.isError && rolls.items.length === 0 && (
          <EmptyState title={t('labels.rolls.empty')} detail={t('labels.rolls.empty.detail')} />
        )}

        {rolls.items.length > 0 && (
          <>
            <Table caption={t('labels.rolls.description')}>
              <thead>
                <tr>
                  <Th>{t('labels.rolls.column.code')}</Th>
                  <Th>{t('labels.rolls.column.order')}</Th>
                  <Th>{t('labels.rolls.column.serials')}</Th>
                  <Th className="text-right">{t('labels.rolls.column.units')}</Th>
                  <Th>{t('labels.rolls.column.state')}</Th>
                  <Th>{t('labels.rolls.column.supplier')}</Th>
                  <Th>{t('labels.rolls.column.updated')}</Th>
                </tr>
              </thead>
              <tbody>
                {rolls.items.map((roll) => (
                  <tr key={roll.id}>
                    <Td>
                      <Link
                        to={`/labels/rolls/${String(roll.id)}`}
                        className="font-mono font-medium text-accent underline underline-offset-2"
                      >
                        {roll.roll_code ?? `#${String(roll.id)}`}
                      </Link>
                    </Td>
                    <Td className="text-ink-muted">
                      {roll.label_order_id === null || roll.label_order_id === undefined ? (
                        '—'
                      ) : (
                        <Link
                          to={`/labels/orders/${String(roll.label_order_id)}`}
                          className="text-accent underline underline-offset-2"
                        >
                          #{roll.label_order_id}
                        </Link>
                      )}
                    </Td>
                    <Td className="font-mono text-xs text-ink-muted">
                      {serialRange(roll.serial_from, roll.serial_to, (value) =>
                        fmt.number(value),
                      ) ?? '—'}
                    </Td>
                    <Td className="text-right tabular-nums">{fmt.number(roll.unit_count)}</Td>
                    <Td>
                      <Badge tone={isRollState(roll.state) ? ROLL_STATE_TONES[roll.state] : 'neutral'}>
                        {isRollState(roll.state) ? t(ROLL_STATE_LABEL_KEYS[roll.state]) : roll.state}
                      </Badge>
                    </Td>
                    <Td className="text-ink-muted">{roll.supplier ?? '—'}</Td>
                    <Td className="text-ink-muted">{fmt.dateTime(roll.updated_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <CardBody className="flex items-center justify-center border-t border-border">
              {rolls.hasMore ? (
                <Button size="sm" onClick={rolls.loadMore} disabled={rolls.isLoadingMore}>
                  {rolls.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>
    </div>
  );
}
